// Vendor libraries
import { ElementRef, EventEmitter } from '@angular/core';
import { fromEvent } from 'rxjs';
import { debounceTime, map, switchMap, takeWhile } from 'rxjs/operators';
import { Store } from '@ngrx/store';
// Constants
import { AUTOCOMPLETER_STATE_SLICE_KEY } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.reducer';
// NgRx actions
import { resetAutocompleter, selectResultByIndex, setAutocompleteCategoriesResults, setAutocompleteListResults, setAutocompleteSelectedIndex, } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.actions';
// Services
import { SearchService } from 'SearchApp/ts/services/search.service';
// Components
import { SearchBarComponent } from '../../base-autocomplete/components/search-bar.component';
import * as i0 from "@angular/core";
import * as i1 from "SearchApp/ts/services/search.service";
import * as i2 from "@ngrx/store";
const _c0 = ["textInput"];
/**
 * Multi Item Search Bar Component
 *
 * This component is used as part of the MultiItemAutocompleteComponent to provide the ability to submit multiple
 * items at once. It differs from the SearchBarComponent by not automatically selecting the top autocomplete result,
 * and by emitting the input text split into an array of strings if it is submitted without a result in the autocomplete dropdown being selected.
 *
 * Output Properties:
 *  multiSubmitHandler: emits the input text split into an array of strings when the enter key is pressed or the add
 *                      button is clicked. For example, 'AAPL,TSLA,VTSAX' becomes ['AAPL', 'TSLA', 'VTSAX']
 */
export class MultiItemSearchBarComponent extends SearchBarComponent {
    constructor(searchService, store) {
        super(searchService, store);
        this.store = store;
        this.multiSubmitHandler = new EventEmitter();
        this.inputText = '';
        this.selectedIndex = -1;
        this.mapStateToComponent = (state) => {
            // mapStateToComponent in the parent SearchBarComponent class is called because this class calls super() in the constructor, so
            // mapStateToComponent here only needs to handle things specific to the multi item submit functionality that this class adds.
            const autocompleterState = state[this.autocompleterId];
            if (!autocompleterState) {
                return;
            }
            this.inputText = autocompleterState.inputText;
            this.selectedIndex = autocompleterState.selectedIndex;
        };
        this.store
            .select(AUTOCOMPLETER_STATE_SLICE_KEY)
            .pipe(takeWhile(() => this.isAlive))
            .subscribe(this.mapStateToComponent);
    }
    // Overriding ngAfterViewInit to prevent the selectedIndex from being set to the top result
    ngAfterViewInit() {
        // Subscribe to changes in the input. Whenever a new value is emitted, call `getData`
        const typeAhead = fromEvent(this.textInput.nativeElement, 'input').pipe(map((e) => e.target.value), debounceTime(200), switchMap((searchTerm) => this.getData(searchTerm)));
        typeAhead
            .pipe(takeWhile(() => this.isAlive))
            .subscribe((data) => {
            this.cacheData[this.cacheKey] = data;
            if (Array.isArray(data)) {
                this.store.dispatch(setAutocompleteListResults({
                    autocompleterId: this.autocompleterId,
                    results: data,
                }));
            }
            else {
                this.store.dispatch(setAutocompleteCategoriesResults({
                    autocompleterId: this.autocompleterId,
                    results: data,
                    extraLinks: [],
                }));
            }
            // Don't automatically select the top result
            this.store.dispatch(setAutocompleteSelectedIndex({ autocompleterId: this.autocompleterId, selectedIndex: -1 }));
        });
    }
    handleFocus() {
        super.handleFocus();
        this.store.dispatch(setAutocompleteSelectedIndex({ autocompleterId: this.autocompleterId, selectedIndex: -1 }));
    }
    enterPress() {
        this.selectedItems();
    }
    selectedItems() {
        // If no result index is selected then emit the input text split into an array of strings.
        // The parent component of this autocompleter can then process all elements in the array
        // as separate items.
        if (this.selectedIndex === -1 && this.inputText.length) {
            // Allow ',' ';', and ' ' as delimiters
            this.multiSubmitHandler.emit(this.inputText.split(/[,; ]+/));
            this.store.dispatch(resetAutocompleter({ autocompleterId: this.autocompleterId }));
        }
        else {
            this.store.dispatch(selectResultByIndex({ autocompleterId: this.autocompleterId }));
        }
    }
}
MultiItemSearchBarComponent.ɵfac = function MultiItemSearchBarComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || MultiItemSearchBarComponent)(i0.ɵɵdirectiveInject(i1.SearchService), i0.ɵɵdirectiveInject(i2.Store)); };
MultiItemSearchBarComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: MultiItemSearchBarComponent, selectors: [["ycn-multi-item-search-bar"]], viewQuery: function MultiItemSearchBarComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.textInput = _t.first);
    } }, outputs: { multiSubmitHandler: "multiSubmitHandler" }, standalone: false, features: [i0.ɵɵInheritDefinitionFeature], decls: 5, vars: 1, consts: [["textInput", ""], ["autocomplete", "off", "name", "q", "type", "text", 1, "form-control", "field-input", 3, "keydown.escape", "keydown.enter", "keydown.arrowUp", "keydown.arrowDown", "blur", "focus", "placeholder"], [1, "input-group-btn"], ["tabindex", "-1", 1, "btn", "btn-secondary", 3, "click", "mouseenter", "mouseleave"]], template: function MultiItemSearchBarComponent_Template(rf, ctx) { if (rf & 1) {
        const _r1 = i0.ɵɵgetCurrentView();
        i0.ɵɵelementStart(0, "input", 1, 0);
        i0.ɵɵlistener("keydown.escape", function MultiItemSearchBarComponent_Template_input_keydown_escape_0_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.escPress()); })("keydown.enter", function MultiItemSearchBarComponent_Template_input_keydown_enter_0_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.enterPress()); })("keydown.arrowUp", function MultiItemSearchBarComponent_Template_input_keydown_arrowUp_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.prevItem($event)); })("keydown.arrowDown", function MultiItemSearchBarComponent_Template_input_keydown_arrowDown_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.nextItem($event)); })("blur", function MultiItemSearchBarComponent_Template_input_blur_0_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleBlur()); })("focus", function MultiItemSearchBarComponent_Template_input_focus_0_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleFocus()); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(2, "span", 2)(3, "div", 3);
        i0.ɵɵlistener("click", function MultiItemSearchBarComponent_Template_div_click_3_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.selectedItems()); })("mouseenter", function MultiItemSearchBarComponent_Template_div_mouseenter_3_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.setShouldBlur(false)); })("mouseleave", function MultiItemSearchBarComponent_Template_div_mouseleave_3_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.setShouldBlur(true)); });
        i0.ɵɵtext(4, " Add ");
        i0.ɵɵelementEnd()();
    } if (rf & 2) {
        i0.ɵɵproperty("placeholder", ctx.placeholder);
    } }, encapsulation: 2 });
